import React, {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import moment from 'moment';
import {AiFillDelete, AiFillEdit, AiOutlinePlus} from 'react-icons/ai';
import {IoMdEye} from 'react-icons/io';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';
import {Switch} from '@headlessui/react';

import CustomTable from '../../components/common/CustomTable';
import InputField from '../../components/ui/InputField';
import AlertModal from '../../components/AlertModal';
import Button from '../../components/ui/Button';
import {
  useGetAssessmentsQuery,
  useDeleteAssessmentMutation,
  useUpdateResultPublishedMutation,
} from '../../api/assessment';
import {pageRoutes} from '../../constants/pageRoutes';
import useAuthStore from '../../store/useAuthStore';

const AssessmentList = () => {
  const [removeAssessmentId, setRemoveAssessmentId] = useState(null);
  const [formData, setFormData] = useState({
    search: '',
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
  });

  const {handleSubmit, watch, register} = useForm({
    mode: 'all',
  });

  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isInvigilator = user?.role === 'invigilator';

  // Fetch assessments using React Query
  const {
    data: assessmentsData,
    isLoading: isLoadingAssessments,
    refetch,
  } = useGetAssessmentsQuery(formData);

  // Extract assessments from API response
  const assessments = assessmentsData?.data?.assessments || [];
  const totalItems = assessmentsData?.data?.pagination?.total || 0;
  const totalPages = assessmentsData?.data?.pagination?.pages || 0;

  // Mutations for CRUD operations
  const {mutate: deleteAssessmentMutate, isLoading: isDeleteLoading} =
    useDeleteAssessmentMutation();
  const {mutate: updateResultPublishedMutate, isLoading: isUpdatingPublish} =
    useUpdateResultPublishedMutation();

  useEffect(() => {
    if (!isLoadingAssessments && !assessments.length && assessmentsData) {
      setFormData((prev) => ({
        ...prev,
        page: prev.page === 1 ? 1 : prev.page - 1,
      }));
    }
  }, [assessmentsData]);

  const handleFormSubmission = async (values) => {
    const {search} = values;
    setFormData((prev) => ({
      ...prev,
      page: 1,
      search,
    }));
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const handleDelete = () => {
    deleteAssessmentMutate(removeAssessmentId, {
      onSuccess: () => {
        setRemoveAssessmentId(null);
        toast.success('Assessment deleted successfully');
        refetch();
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || 'Failed to delete assessment',
        );
      },
    });
  };

  const handleResultPublishedToggle = (assessmentId, currentValue) => {
    updateResultPublishedMutate(
      {
        assessmentId,
        resultPublished: !currentValue,
      },
      {
        onSuccess: () => {
          toast.success('Results publication status updated successfully');
          refetch();
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message ||
              'Failed to update results publication status',
          );
        },
      },
    );
  };

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  const handleCreateAssessment = () => {
    navigate(pageRoutes.createAssessment);
  };

  const handleEditAssessment = (assessment) => {
    navigate(`${pageRoutes.editAssessment}/${assessment._id}`);
  };

  const tHeadData = [
    {
      id: 1,
      keyIndex: 'name',
      label: 'Assessment Name',
      sort: true,
      width: '20%',
    },
    {
      id: 2,
      keyIndex: 'slug',
      label: 'Slug',
      sort: true,
      width: '15%',
    },
    {
      id: 3,
      keyIndex: 'collegeName',
      label: 'College',
      sort: true,
      width: '15%',
      render: (college) => college || '-',
    },
    {
      id: 4,
      keyIndex: 'startDateTime',
      label: 'Start Date',
      sort: true,
      width: '15%',
      render: (date, record) =>
        record.startDateTimeFormatted ||
        moment(date).utcOffset('+05:30').format('DD-MM-YYYY h:mm a'),
    },
    {
      id: 5,
      keyIndex: 'endDateTime',
      label: 'End Date',
      sort: true,
      width: '15%',
      render: (date, record) =>
        record.endDateTimeFormatted ||
        moment(date).utcOffset('+05:30').format('DD-MM-YYYY h:mm a'),
    },
    {
      id: 8,
      keyIndex: 'resultPublished',
      label: 'Publish Results',
      width: '10%',
      render: (resultPublished, record) => {
        const isEditable = new Date() > new Date(record.startDateTime);
        return (
          <div className="flex items-center">
            <Switch
              checked={resultPublished}
              onChange={() =>
                handleResultPublishedToggle(record._id, resultPublished)
              }
              disabled={!isEditable || isUpdatingPublish}
              className={`${
                resultPublished ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  resultPublished ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
            <span className="ml-2 text-sm text-gray-600">
              {resultPublished ? 'Published' : 'Not Published'}
            </span>
          </div>
        );
      },
    },
    {
      id: 9,
      keyIndex: 'status',
      label: 'Status',
      width: '10%',
      render: (status) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium
          ${
            status === 'upcoming'
              ? 'bg-blue-100 text-blue-800'
              : status === 'ongoing'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      id: 10,
      keyIndex: '_id',
      label: 'Action',
      width: '10%',
      render: (id, record) => {
        const isEditable = new Date() < new Date(record.startDateTime);

        return (
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="view candidates"
              onClick={() =>
                navigate(`${pageRoutes.assessments}/${id}/candidates`)
              }
              className="text-blue-600 hover:text-blue-800"
            >
              <IoMdEye size={23} />
            </button>

            {isEditable && !isInvigilator && (
              <button
                type="button"
                aria-label="edit assessment"
                onClick={() => handleEditAssessment(record)}
                className="text-green-600 hover:text-green-800"
              >
                <AiFillEdit size={20} />
              </button>
            )}

            {!isInvigilator && (
              <button
                type="button"
                aria-label="delete assessment"
                onClick={() => setRemoveAssessmentId(id)}
                className="text-red-600 hover:text-red-800"
              >
                <AiFillDelete size={20} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AlertModal
        type="warning"
        isOpen={removeAssessmentId}
        onClose={() => setRemoveAssessmentId(null)}
        title="Confirm this action"
        onCancel={() => setRemoveAssessmentId(null)}
        onOK={handleDelete}
        okLoading={isDeleteLoading}
      >
        Are you sure you want to delete this assessment?
      </AlertModal>

      <div className="px-8 py-6">
        <div className="flex items-center gap-2">
          <h3 className="text-2xl font-bold">
            Assessments {!!Number(totalItems) && `(${totalItems})`}
          </h3>
        </div>
        <form className="py-5">
          <div className="flex justify-between">
            <div className="flex gap-4">
              <InputField
                {...register('search')}
                type="search"
                placeholder="Search"
                className="w-60"
              />
            </div>

            <div className="flex items-center gap-3">
              {!isInvigilator && (
                <Button
                  className="py-3 px-5 flex flex-row items-center gap-2"
                  onClick={handleCreateAssessment}
                >
                  <AiOutlinePlus size={18} />
                  Add Assessment
                </Button>
              )}
            </div>
          </div>
        </form>

        <div className="bg-[white] p-10 border">
          <CustomTable
            tHeadData={tHeadData}
            tBodyData={assessments}
            handleSort={handleSort}
            pageCount={totalPages}
            currentPage={formData.page}
            loading={isLoadingAssessments}
          />
        </div>
      </div>
    </>
  );
};

export default AssessmentList;
